import { logger } from 'services/logger'
import { isStorageQuotaExceededError } from 'services/StorageManager'
import { errorToString, getErrorName } from 'utils/errorUtils'
import type { PhotoBookStorageBackendName } from './photoBookStorageBackend.types'

/**
 * The retry's own failure is the cause; the error that killed the first attempt
 * travels alongside it. The first says what closed the connection or aborted
 * the transaction, the cause says whether a fresh connection fared any better.
 */
export class PhotoBookDbRetryError extends Error {
  readonly firstAttemptError: unknown

  constructor(firstAttemptError: unknown, retryError: unknown) {
    super(
      'Photo book database operation failed again on a reopened connection',
      { cause: retryError }
    )
    this.name = 'PhotoBookDbRetryError'
    this.firstAttemptError = firstAttemptError
  }
}

export class PhotoBookDbTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Photo book database read did not settle within ${timeoutMs}ms`)
    this.name = 'PhotoBookDbTimeoutError'
  }
}

// WebKit reports every backing-store problem as UnknownError, so only the
// message tells them apart. This one: Safari wiped the origin's data.
const WEBKIT_DATABASE_DELETED_MESSAGE =
  'Database deleted by request of the user'

// WebKit open-time failures ("Error creating <table> table (<code>) - <text>"
// covers any broken file) and Chromium's failed backing-store open.
const STORAGE_UNAVAILABLE_UNKNOWN_ERROR_MESSAGES = [
  'Error creating ',
  'database or disk is full',
  'Unable to open database file on disk',
  'Unable to establish IDB database file',
  'IDBDatabaseInfo table does not exist',
  'Failed to prepare statement for getting metadata version',
  'Cannot update database metadata version',
  'database name does not match requested name',
  'Encountered full disk while opening backing store',
  'Internal error opening backing store',
]

const isUnknownErrorWithMessage = (
  err: unknown,
  messageParts: string[]
): err is Error =>
  err instanceof Error &&
  err.name === 'UnknownError' &&
  messageParts.some((part) => err.message.includes(part))

// Browsers without IndexedDB at all: "indexedDB is not defined" (Chromium,
// Firefox), "Can't find variable: indexedDB" (WebKit).
const isMissingIndexedDbError = (err: unknown): boolean =>
  err instanceof ReferenceError && err.message.includes('indexedDB')

type PhotoBookDbFailureKind =
  | 'storage-unavailable'
  | 'storage-full'
  | 'storage-cleared'
  | 'page-unloading'
  | 'timed-out'
  | 'unexpected'

function classifyPhotoBookDbFailure(
  err: unknown,
  visibilityState: DocumentVisibilityState,
  storageBackend: PhotoBookStorageBackendName
): PhotoBookDbFailureKind {
  if (err instanceof PhotoBookDbTimeoutError) {
    return 'timed-out'
  }
  // A full localStorage is our own cap. A full IndexedDB is a full disk.
  if (isStorageQuotaExceededError(err) && storageBackend === 'local-storage') {
    return 'storage-full'
  }
  if (
    isStorageQuotaExceededError(err) ||
    isUnknownErrorWithMessage(
      err,
      STORAGE_UNAVAILABLE_UNKNOWN_ERROR_MESSAGES
    ) ||
    isMissingIndexedDbError(err)
  ) {
    return 'storage-unavailable'
  }
  if (isUnknownErrorWithMessage(err, [WEBKIT_DATABASE_DELETED_MESSAGE])) {
    return 'storage-cleared'
  }
  // The browser aborts transactions under a tab it is unloading or freezing;
  // nobody is left to see the editor.
  if (getErrorName(err) === 'AbortError' && visibilityState === 'hidden') {
    return 'page-unloading'
  }
  return 'unexpected'
}

type PhotoBookDbFailureReport = {
  failureKind: PhotoBookDbFailureKind
  errorName: string
  errorMessage: string
  wasRetriedOnReopenedConnection: boolean
  firstAttemptErrorName: string | null
  firstAttemptErrorMessage: string | null
  visibilityState: DocumentVisibilityState
  msSincePageLoad: number
  isIndexedDbAvailable: boolean
}

// A hidden page at failure time points at a suspended or unloading tab, and a
// failure a few milliseconds into the page at a navigation that cut the load
// short.
function describePhotoBookDbFailure(
  err: unknown,
  storageBackend: PhotoBookStorageBackendName
): PhotoBookDbFailureReport {
  const retryError = err instanceof PhotoBookDbRetryError ? err : null
  const failedError = retryError ? retryError.cause : err
  const { visibilityState } = document

  return {
    failureKind: classifyPhotoBookDbFailure(
      failedError,
      visibilityState,
      storageBackend
    ),
    errorName: getErrorName(failedError),
    errorMessage: errorToString(failedError),
    wasRetriedOnReopenedConnection: retryError !== null,
    firstAttemptErrorName: retryError
      ? getErrorName(retryError.firstAttemptError)
      : null,
    firstAttemptErrorMessage: retryError
      ? errorToString(retryError.firstAttemptError)
      : null,
    visibilityState,
    msSincePageLoad: Math.round(performance.now()),
    isIndexedDbAvailable: typeof indexedDB !== 'undefined',
  }
}

type PhotoBookDbOperation = 'load' | 'persist' | 'delete'

const OPERATION_FAILURE_MESSAGES: Record<PhotoBookDbOperation, string> = {
  load: 'Failed to load the stored photo books',
  persist: 'Error persisting photo book to storage',
  delete: 'Error deleting the stored photo book',
}

const FAILURE_KIND_TITLE_SUFFIXES: Record<
  Exclude<PhotoBookDbFailureKind, 'unexpected'>,
  string
> = {
  'storage-unavailable': 'storage unavailable',
  'storage-full': 'local storage is full',
  'storage-cleared': 'storage cleared by the browser',
  'page-unloading': 'aborted on a hidden page',
  'timed-out': 'timed out',
}

const ERROR_LEVEL_FAILURE_KINDS: PhotoBookDbFailureKind[] = [
  'unexpected',
  'storage-full',
]

type PhotoBookDbFailureContext = {
  storageBackend: PhotoBookStorageBackendName
  bookId?: string
  loadStep?: string
}

// Sentry groups by the logger's wrapper stack, identical for every failure, so
// the fingerprint splits the browser's storage problems from failures that may
// be ours. Only the latter, and a book that outgrew the localStorage quota, go
// out at error level.
export function reportPhotoBookDbFailure(
  operation: PhotoBookDbOperation,
  err: unknown,
  { storageBackend, bookId, loadStep }: PhotoBookDbFailureContext
): PhotoBookDbFailureReport {
  const failure = describePhotoBookDbFailure(err, storageBackend)
  const { failureKind } = failure
  const message =
    failureKind === 'unexpected'
      ? OPERATION_FAILURE_MESSAGES[operation]
      : `${OPERATION_FAILURE_MESSAGES[operation]} (${FAILURE_KIND_TITLE_SUFFIXES[failureKind]})`

  logger.error(message, err, {
    ...failure,
    storageBackend,
    ...(bookId ? { bookId } : null),
    ...(loadStep ? { loadStep } : null),
    tags: {
      failureKind,
      errorName: failure.errorName,
      storageBackend,
      ...(loadStep ? { loadStep } : null),
    },
    fingerprint: ['photo-book-db', operation, failureKind],
    level: ERROR_LEVEL_FAILURE_KINDS.includes(failureKind)
      ? 'error'
      : 'warning',
  })
  return failure
}
